import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowUpDown, Check, PackageSearch, SlidersHorizontal, Star, Store, X } from 'lucide-react';
import { ShopChrome, Crumbs } from '@/components/shop/ShopChrome';
import ProductCard, { ProductCardSkeleton, ProductGrid } from '@/components/shop/ProductCard';
import FilterPanel from '@/components/shop/FilterPanel';
import { Pagination, Sheet } from '@/components/shop/ShopUI';
import { EmptyState, Shimmer } from '@/components/ui';
import { getStore, searchProducts } from '@/api/shop';
import { friendlyError } from '@/lib/apiError';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import { SORT_LABELS, appliedChips, isFilterParam, istDate, isLightHex } from '@/lib/shop';

/**
 * One page for a category (/shop/c/:slug), a search (/shop/search?q=) and a
 * store (/shop/store/:slug). The URL's query string *is* the API query, so
 * filters, sort and page survive reloads, back/forward and sharing.
 */
const ShopListing = ({ mode = 'category' }) => {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);

  const query = useMemo(() => {
    const q = new URLSearchParams(params);
    if (mode === 'category' && slug) q.set('category', slug);
    return q.toString();
  }, [params, mode, slug]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    const req = mode === 'store' ? getStore(slug, query) : searchProducts(query);
    req
      .then((d) => live && setResult(d))
      .catch((err) => {
        if (!live) return;
        setError({ status: err?.response?.status, message: friendlyError(err, 'Products could not be loaded') });
        setResult(null);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [query, mode, slug]);

  /** Every filter/sort change goes through here: copy the URL, mutate, reset to page 1. */
  const update = useCallback(
    (mutate, { keepPage = false } = {}) => {
      const next = new URLSearchParams(params);
      mutate(next);
      if (!keepPage) next.delete('page');
      setParams(next);
    },
    [params, setParams]
  );

  const clearAll = () =>
    update((p) => {
      [...p.keys()].filter(isFilterParam).forEach((k) => p.delete(k));
    });

  const facets = result?.facets || [];
  const chips = appliedChips(params, facets);
  const items = result?.items || [];
  const pagination = result?.pagination || {};
  const category = result?.category;
  const seller = result?.seller;
  const sort = params.get('sort') || result?.sort || '';
  const sorts = result?.sorts || [];
  const page = Number(params.get('page')) || 1;
  const q = params.get('q') || '';

  const title =
    mode === 'store' ? seller?.name : mode === 'search' ? (q ? `Results for “${q}”` : 'All products') : category?.name;

  const total = pagination.total;
  useDocumentMeta(
    mode === 'store'
      ? seller && {
          title: `${seller.name}${seller.city ? `, ${seller.city}` : ''} — LK Shop`,
          description: seller.description || `Shop ${seller.name}${seller.city ? ` in ${seller.city}` : ''} on LK Shop. Verified local store, secure payments, easy returns.`,
          image: seller.banner || seller.logo,
        }
      : mode === 'search'
        ? {
            title: q ? `“${q}” — LK Shop` : 'All products — LK Shop',
            description: q
              ? `${total ? `${total} result${total === 1 ? '' : 's'}` : 'Results'} for “${q}” from local stores on LK Shop.`
              : 'Browse every product from local stores on LK Shop.',
          }
        : category && {
            title: `${category.name} — LK Shop`,
            description: category.description || `Shop ${category.name.toLowerCase()} from verified local stores on LK Shop.`,
            image: category.image,
          }
  );

  const crumbs = [
    { name: 'Shop', to: '/shop' },
    ...(mode === 'store'
      ? [{ name: 'Stores' }, { name: seller?.name || '…' }]
      : mode === 'search'
        ? [{ name: q ? `Search: ${q}` : 'All products' }]
        : (category?.breadcrumbs || []).map((b) => ({ name: b.name, to: `/shop/c/${b.slug}` }))),
  ];

  const goPage = (n) => {
    update((p) => (n > 1 ? p.set('page', String(n)) : p.delete('page')), { keepPage: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (error?.status === 404) {
    return (
      <ShopChrome>
        <div className="lk-container py-12">
          <EmptyState
            icon={mode === 'store' ? Store : PackageSearch}
            title={mode === 'store' ? 'Store not found' : 'Category not found'}
            message="It may have moved or is no longer available."
            action={<Link to="/shop" className="lk-btn-primary">Back to the shop</Link>}
          />
        </div>
      </ShopChrome>
    );
  }

  const firstLoad = loading && !result;

  return (
    <ShopChrome>
      {/* Store banner */}
      {mode === 'store' && (
        <section className="border-b border-ink-200/70 bg-white">
          <div className="lk-container flex items-center gap-4 py-5">
            {seller?.logo ? (
              <img src={seller.logo} alt="" className="h-16 w-16 rounded-2xl object-cover ring-1 ring-ink-200" />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-tint text-brand-secondary">
                <Store className="h-8 w-8" />
              </span>
            )}
            <div className="min-w-0">
              {seller ? (
                <>
                  <h1 className="truncate text-h2 text-ink-900">{seller.name}</h1>
                  <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-500">
                    {seller.rating > 0 && (
                      <span className="inline-flex items-center gap-1 font-semibold text-ink-900">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        {Number(seller.rating).toFixed(1)}
                        <span className="font-normal text-ink-500">({seller.ratingCount} ratings)</span>
                      </span>
                    )}
                    {seller.city && <span>{seller.city}</span>}
                    {seller.since && <span>Selling since {istDate(seller.since, { month: 'short', year: 'numeric' })}</span>}
                  </p>
                  {seller.description && <p className="mt-1 line-clamp-2 text-sm text-ink-700">{seller.description}</p>}
                </>
              ) : (
                <>
                  <Shimmer className="h-7 w-48" />
                  <Shimmer className="mt-2 h-4 w-64" />
                </>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="lk-container py-4 sm:py-6">
        <Crumbs items={crumbs} />

        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {mode !== 'store' &&
              (title ? <h1 className="text-h2 text-ink-900">{title}</h1> : <Shimmer className="h-8 w-56" />)}
            <p className="tabular mt-0.5 text-sm text-ink-500">
              {firstLoad ? ' ' : `${(pagination.total || 0).toLocaleString('en-IN')} product${pagination.total === 1 ? '' : 's'}`}
              {pagination.capped ? ' (showing the top results)' : ''}
            </p>
          </div>

          {/* Desktop sort */}
          {sorts.length > 0 && (
            <label className="hidden items-center gap-2 text-sm text-ink-500 lg:flex">
              Sort by
              <select
                value={sort}
                onChange={(e) => update((p) => p.set('sort', e.target.value))}
                className="lk-focus rounded-xl border border-ink-200 bg-white py-2 pl-3 pr-8 text-sm font-semibold text-ink-900"
              >
                {sorts.map((s) => (
                  <option key={s} value={s}>
                    {SORT_LABELS[s] || s}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {/* Sub-categories with counts */}
        {category?.children?.length > 0 && (
          <div className="scrollbar-hide -mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0">
            {category.children.map((c) => (
              <Link key={c.slug} to={`/shop/c/${c.slug}`} className="lk-chip lk-chip-idle min-h-[40px] py-1.5">
                {c.name}
                <span className="tabular text-xs text-ink-400">{c.count}</span>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-5 flex gap-6">
          {/* Desktop filter sidebar */}
          <aside className="hidden w-64 shrink-0 lg:block">
            <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto rounded-2xl border border-ink-200/70 bg-white px-4 py-2">
              <div className="flex items-center justify-between border-b border-ink-200/70 py-2">
                <span className="text-sm font-semibold text-ink-900">Filters</span>
                {chips.length > 0 && (
                  <button type="button" onClick={clearAll} className="lk-focus rounded text-xs font-semibold text-brand-secondary">
                    Clear all
                  </button>
                )}
              </div>
              {firstLoad ? (
                <div className="space-y-3 py-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <Shimmer key={i} className="h-20 w-full" />
                  ))}
                </div>
              ) : (
                <FilterPanel facets={facets} params={params} onChange={update} />
              )}
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            {chips.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {chips.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => update(c.remove)}
                    className="lk-focus inline-flex items-center gap-1.5 rounded-full border border-brand-secondary/30 bg-brand-tint py-1.5 pl-3 pr-2 text-xs font-semibold text-brand-secondary transition hover:bg-brand-secondary hover:text-white"
                  >
                    {c.hex && <span className={`h-3 w-3 rounded-full ${isLightHex(c.hex) ? 'ring-1 ring-ink-200' : ''}`} style={{ backgroundColor: c.hex }} />}
                    {c.label}
                    <X className="h-3.5 w-3.5" aria-label="Remove" />
                  </button>
                ))}
                <button type="button" onClick={clearAll} className="lk-focus rounded px-2 py-1 text-xs font-semibold text-ink-500 hover:text-ink-900">
                  Clear all
                </button>
              </div>
            )}

            {error ? (
              <EmptyState icon={PackageSearch} title="Something went wrong" message={error.message} />
            ) : firstLoad ? (
              <ProductGrid>
                {Array.from({ length: 8 }, (_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </ProductGrid>
            ) : items.length === 0 ? (
              <EmptyState
                icon={PackageSearch}
                title={chips.length ? 'No products match these filters' : q ? `Nothing found for “${q}”` : 'Nothing here yet'}
                message={chips.length ? 'Try removing a filter or two.' : 'Try a different word, or browse the categories.'}
                action={
                  chips.length ? (
                    <button type="button" onClick={clearAll} className="lk-btn-primary">
                      Clear all filters
                    </button>
                  ) : (
                    <Link to="/shop" className="lk-btn-primary">
                      Browse the shop
                    </Link>
                  )
                }
              />
            ) : (
              <div className={`transition-opacity ${loading ? 'opacity-50' : ''}`} aria-busy={loading}>
                <ProductGrid>
                  {items.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </ProductGrid>
                <Pagination page={page} lastPage={pagination.lastPage} onChange={goPage} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Phones: sort + filter pinned to the bottom of the screen. */}
      <div className="h-16 lg:hidden" />
      <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-2 border-t border-ink-200 bg-white shadow-bar lg:hidden">
        <button type="button" onClick={() => setSortOpen(true)} className="flex min-h-[56px] items-center justify-center gap-2 border-r border-ink-200 text-sm font-semibold text-ink-900">
          <ArrowUpDown className="h-4 w-4" />
          Sort
        </button>
        <button type="button" onClick={() => setFiltersOpen(true)} className="flex min-h-[56px] items-center justify-center gap-2 text-sm font-semibold text-ink-900">
          <SlidersHorizontal className="h-4 w-4" />
          Filter
          {chips.length > 0 && <span className="rounded-full bg-brand-secondary px-1.5 text-[11px] font-bold leading-5 text-white">{chips.length}</span>}
        </button>
      </div>

      <Sheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filters"
        footer={
          <div className="flex gap-3">
            <button type="button" onClick={clearAll} disabled={!chips.length} className="lk-btn-ghost flex-1">
              Clear all
            </button>
            <button type="button" onClick={() => setFiltersOpen(false)} className="lk-btn-primary flex-1">
              {loading ? 'Updating…' : `Show ${pagination.total || 0} results`}
            </button>
          </div>
        }
      >
        <FilterPanel facets={facets} params={params} onChange={update} />
      </Sheet>

      <Sheet open={sortOpen} onClose={() => setSortOpen(false)} title="Sort by">
        <div className="space-y-1 pb-2">
          {sorts.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                update((p) => p.set('sort', s));
                setSortOpen(false);
              }}
              className={`flex min-h-[48px] w-full items-center justify-between rounded-xl px-3 text-left text-sm ${
                s === sort ? 'bg-brand-tint font-semibold text-brand-secondary' : 'text-ink-700 hover:bg-surface-muted'
              }`}
            >
              {SORT_LABELS[s] || s}
              {s === sort && <Check className="h-4 w-4" />}
            </button>
          ))}
        </div>
      </Sheet>
    </ShopChrome>
  );
};

export default ShopListing;
